import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Alert, Button, Tooltip } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useState } from "react";
import * as Yup from "yup";
import CustomTextArea from "@/components/CustomTextArea";
import Flex from "@/components/Flex";
import StyledDialog from "@/components/StyledDialog";
import Text from "@/components/Text";
import ConfirmationModal from "@/components/app/ConfirmationModal";
import SignatureInput from "@/components/signiture/SignatureInput";
import UploadSignatureButton from "@/components/signiture/UploadSignatureButton";
import useDisclosure, { DisclosureHost } from "@/hooks/use-disclosure";
import { useSnackbar } from "@/hooks/useSnackbar";
import { templateTitle } from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-preview/note-preview-utils";
import {
  ENCOUNTER_SUMMARY_REPORT_QUERY_KEY,
  useEncounterSummaryReport,
} from "@/queries/encounter/use-encounter-summary-report";
import { useSignAddendum } from "@/queries/encounter/use-sign-addendum";
import { formatDate } from "@/utils/date-time-utility";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "@/utils/utils";
import { useAddendumSignature } from "./hooks/use-addendum-signature";
import { ADDENDUM_LABELS, ADDENDUM_MAX_LENGTH } from "./visit-detail-constant";

export interface AddAddendumModalProps {
  /** Opened with the appointment id: `disclosure.open(appointmentId)`. */
  disclosure: DisclosureHost;
  onSigned: () => void;
}

const AddAddendumModal = ({ disclosure, onSigned }: AddAddendumModalProps) => {
  const appointmentUuid: string | undefined = disclosure.data;
  const showSnackbar = useSnackbar();
  const discardDisc = useDisclosure();
  const [step, setStep] = useState<"text" | "sign">("text");
  const {
    canvasRef,
    signPad,
    savedSignature,
    hasSignature,
    signatureError,
    markSigned,
    importSignature,
    clearSignature,
    resetSignature,
  } = useAddendumSignature(disclosure.opened && step === "sign");
  const { mutateAsync: signAddendum } = useSignAddendum();
  const queryClient = useQueryClient();
  const { data: report } = useEncounterSummaryReport({
    appointmentUuid,
    xTENANTID: getXtenantId(),
    enabled: disclosure.opened,
  });

  const formik = useFormik({
    initialValues: { addendumText: "" },
    validationSchema: Yup.object({
      addendumText: Yup.string().trim().required(ADDENDUM_LABELS.textRequired),
    }),
    onSubmit: () => setStep("sign"),
  });

  const close = () => {
    formik.resetForm();
    setStep("text");
    resetSignature();
    disclosure.close();
  };

  /* Cancel and the close icon only ask before throwing typed text away. */
  const requestClose = () =>
    formik.values.addendumText.trim() ? discardDisc.open() : close();

  const save = async () => {
    if (!appointmentUuid || !signPad || signPad.isEmpty()) return;
    try {
      const res = await signAddendum({
        appointmentUuid,
        encounterAddendum: {
          addendumText: formik.values.addendumText.trim(),
          providerSignature: signPad.toDataURL(),
        },
        xTENANTID: getXtenantId(),
      });
      showSnackbar(getSuccessSnackbar(res));
      /* View visit and the PDF read this report; drop the copy loaded before the addendum existed. */
      await queryClient.invalidateQueries({
        queryKey: [ENCOUNTER_SUMMARY_REPORT_QUERY_KEY],
      });
      close();
      onSigned();
    } catch (err) {
      showSnackbar(getErrorSnackbar(err));
    }
  };

  const isAtMaxLength =
    formik.values.addendumText.length >= ADDENDUM_MAX_LENGTH;

  const visitLine = [
    report?.patientName,
    report ? templateTitle(report) : undefined,
    report?.appointmentDate ? formatDate(report.appointmentDate) : undefined,
  ]
    .filter(Boolean)
    .join(" · ");

  if (!disclosure.opened) return null;

  return (
    <>
      <StyledDialog
        open={disclosure.opened}
        onClose={requestClose}
        dialogTitle={ADDENDUM_LABELS.title}
        PaperProps={{ sx: { width: 640, maxWidth: "none" } }}
        dialogAction={
          <Flex gap={1} justifyContent="flex-end" width="100%">
            <Button variant="outlined" onClick={requestClose}>
              {ADDENDUM_LABELS.cancel}
            </Button>
            {step === "text" ? (
              <Button variant="contained" onClick={() => formik.handleSubmit()}>
                {ADDENDUM_LABELS.signAddendum}
              </Button>
            ) : (
              <Button
                variant="contained"
                disabled={!hasSignature}
                onClick={save}
              >
                {ADDENDUM_LABELS.saveAndClose}
              </Button>
            )}
          </Flex>
        }
      >
        <Flex flexDirection="column" gap={2}>
          {visitLine && <Text fontWeight={500}>{visitLine}</Text>}

          <CustomTextArea
            label={ADDENDUM_LABELS.textLabel}
            required
            name="addendumText"
            rows={8}
            placeholder={ADDENDUM_LABELS.textPlaceholder}
            value={formik.values.addendumText}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={step === "sign"}
            inputProps={{ maxLength: ADDENDUM_MAX_LENGTH }}
            error={formik.submitCount > 0 && !!formik.errors.addendumText}
            helperText={
              (formik.submitCount > 0 && formik.errors.addendumText) ||
              (isAtMaxLength ? ADDENDUM_LABELS.maxLengthReached : undefined)
            }
          />

          {step === "sign" && (
            <Flex flexDirection="column" gap={1}>
              <SignatureInput
                required
                label={ADDENDUM_LABELS.signature}
                error={signatureError}
                canvasRef={canvasRef}
                w="590px"
                h="160px"
              />
              <Flex justifyContent="flex-end" alignItems="center" gap={1}>
                <Tooltip
                  title={savedSignature ? "" : ADDENDUM_LABELS.noSavedSignature}
                  placement="top"
                  arrow
                >
                  {/* A disabled button fires no hover events, so the span carries the tooltip. */}
                  <span>
                    <Button
                      variant="outlined"
                      startIcon={<ArrowForwardIcon />}
                      sx={{ height: "24px" }}
                      disabled={!savedSignature || !signPad}
                      onClick={importSignature}
                    >
                      {ADDENDUM_LABELS.importSignature}
                    </Button>
                  </span>
                </Tooltip>
                <UploadSignatureButton
                  signPad={signPad}
                  canvasRef={canvasRef}
                  onUploaded={markSigned}
                />
                <Button
                  variant="contained"
                  sx={{ height: "24px" }}
                  onClick={clearSignature}
                >
                  {ADDENDUM_LABELS.clear}
                </Button>
              </Flex>
            </Flex>
          )}

          <Alert severity="info">{ADDENDUM_LABELS.info}</Alert>
        </Flex>
      </StyledDialog>

      <ConfirmationModal
        disclosureHost={discardDisc}
        title={ADDENDUM_LABELS.discardTitle}
        cancelText={ADDENDUM_LABELS.goBack}
        confirmationText={ADDENDUM_LABELS.discard}
        onConfirm={() => {
          discardDisc.close();
          close();
        }}
      />
    </>
  );
};

export default AddAddendumModal;
